import React from 'react';
import { Box, Container, Typography, Stack, Chip, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import InventoryIcon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BuildIcon from '@mui/icons-material/Build';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TableChartIcon from '@mui/icons-material/TableChart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

const stats = [
  { value: 13, suffix: '', label: 'Productos en inventario' },
  { value: 147, suffix: '', label: 'Ítems registrados' },
  { value: 4.5, suffix: 'M+', label: 'Valor de inventario gestionado', decimals: 1, prefix: '$' },
  { value: 5, suffix: '', label: 'Módulos integrados' },
];

const modules = [
  { icon: <InventoryIcon />, title: 'Dashboard de Inventario', desc: 'Control total de stock, precios de costo vs. cliente y alertas de bajo stock en tiempo real.' },
  { icon: <BuildIcon />, title: 'Órdenes de Servicio', desc: 'Gestión de órdenes pendientes, en proceso y cerradas con historial filtrable por fecha.' },
  { icon: <TrendingUpIcon />, title: 'Ingresos y Gastos', desc: 'Resumen financiero con gráficas, registros detallados y exportación a Excel.' },
  { icon: <RequestQuoteIcon />, title: 'Cotizaciones', desc: 'Generación de presupuestos por cliente y vehículo con control de estado y vencimiento.' },
  { icon: <ReceiptLongIcon />, title: 'Facturación', desc: 'Conversión de órdenes completadas en facturas con historial de búsqueda.' },
  { icon: <PeopleIcon />, title: 'Gestión de Clientes', desc: 'Historial de vehículos y servicios por cliente con seguimiento completo.' },
  { icon: <NotificationsActiveIcon />, title: 'Notificaciones', desc: 'Alertas automáticas de bajo stock, órdenes pendientes y vencimientos.' },
  { icon: <TableChartIcon />, title: 'Reportes y Exportación', desc: 'Exportación a Excel de registros financieros e inventario para análisis externo.' },
];

const StatCard = ({ value, suffix, label, decimals = 0, prefix = '' }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.4 });
  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: { xs: '2.2rem', md: '2.8rem' },
          fontWeight: 800,
          color: '#38BDF8',
          lineHeight: 1,
        }}
      >
        {inView ? (
          <CountUp
            start={0}
            end={value}
            duration={2}
            decimals={decimals}
            prefix={prefix}
            suffix={suffix}
            separator="."
          />
        ) : `${prefix}0${suffix}`}
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
};

const PortfolioSection = () => {
  return (
    <Box id="portfolio" sx={{ bgcolor: 'white', py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="overline"
            display="block"
            align="center"
            sx={{ color: '#38BDF8', fontWeight: 700, letterSpacing: 3, mb: 1 }}
          >
            PORTAFOLIO
          </Typography>
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 800, color: '#083a94', mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}
          >
            Lo que construimos
          </Typography>
          <Typography
            align="center"
            sx={{ color: '#555', maxWidth: 560, mx: 'auto', mb: 8, fontSize: '1.05rem' }}
          >
            Soluciones reales para negocios reales. Aquí un ejemplo de lo que
            diseñamos e implementamos desde cero.
          </Typography>
        </motion.div>

        {/* Tarjeta principal del proyecto */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {/* Header del proyecto */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #083a94 0%, #0d4dbf 100%)',
                p: { xs: 4, md: 6 },
                color: 'white',
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={3}>
                <Box>
                  <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
                    <Chip label="Caso real" size="small" sx={{ bgcolor: '#38BDF8', color: '#083a94', fontWeight: 700 }} />
                    <Chip label="Desarrollo a medida" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                    <Chip label="Sistema completo" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                    Sistema de Gestión de Taller Mecánico
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 540, lineHeight: 1.7 }}>
                    Sistema web completo diseñado y desarrollado a medida para la operación
                    integral de un taller mecánico: desde la recepción del vehículo hasta
                    la facturación final, pasando por inventario, finanzas y clientes.
                  </Typography>
                </Box>
              </Stack>

              {/* Stats */}
              <Box
                sx={{
                  mt: 5,
                  pt: 4,
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: 3,
                }}
              >
                {stats.map((s) => (
                  <StatCard key={s.label} {...s} />
                ))}
              </Box>
            </Box>

            {/* Módulos */}
            <Box sx={{ p: { xs: 4, md: 6 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#083a94', mb: 3 }}>
                Módulos incluidos
              </Typography>
              <Grid container spacing={2} columns={12}>
                {modules.map((mod, i) => (
                  <Grid key={mod.title} span={{ xs: 12, sm: 6, md: 3 }}>
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.07 }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          border: '1px solid #E5E7EB',
                          borderRadius: 2,
                          height: '100%',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            borderColor: '#38BDF8',
                            boxShadow: '0 4px 20px rgba(56,189,248,0.15)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#083a94', mb: 1 }}>{mod.icon}</Box>
                        <Typography sx={{ fontWeight: 700, color: '#083a94', mb: 0.5, fontSize: '0.9rem' }}>
                          {mod.title}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.82rem', lineHeight: 1.5 }}>
                          {mod.desc}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PortfolioSection;
